import type { CSSProperties } from 'react';
import type { Theme } from '@mui/material/styles';

/**
 * The colours every academy diagram draws with, as CSS variables set on the SVG root: the pipeline's bronze, silver
 * and gold, three shared meanings (accent, ok, warn), and the theme's text, muted and paper colours. Each has a light
 * and a dark value, so the same drawing reads well in both modes and the same meaning gets the same colour on every page.
 */
const NAMES = ['fg', 'muted', 'paper', 'bronze', 'silver', 'gold', 'accent', 'ok', 'warn'] as const;
type DiagramColorName = (typeof NAMES)[number];
type FixedColorName = Exclude<DiagramColorName, 'fg' | 'muted' | 'paper'>;

export const DIAGRAM_COLORS: Record<'light' | 'dark', Record<FixedColorName, string>> = {
  light: {
    bronze: '#b5742a',
    silver: '#6c7a89',
    /** Dark enough to read as 14px text on paper (4.6:1); still gold on a shape. */
    gold: '#a07d12',
    /** Lantern itself, and the one box a picture singles out. */
    accent: '#3b6fd6',
    /** Agreement: a corroborated fact, a winning claim, a closed exception. */
    ok: '#2e8b57',
    /** An exception, an audit, a thing that must not happen. */
    warn: '#c0392b',
  },
  dark: {
    bronze: '#d08a3c',
    silver: '#93a0b0',
    gold: '#ddb744',
    accent: '#7aa0ee',
    ok: '#4fb97f',
    warn: '#e4655a',
  },
};

/** `var(--academy-…)` for each colour: what a drawing writes in its fills and strokes. */
export const color = Object.fromEntries(NAMES.map((name) => [name, `var(--academy-${name})`])) as Record<DiagramColorName, string>;

/** The style an SVG root takes: it fills its container and sets every colour variable for the theme's mode. */
export function diagramStyle(theme: Theme): CSSProperties {
  const fixed = DIAGRAM_COLORS[theme.palette.mode];
  const vars: Record<string, string> = {
    '--academy-fg': theme.palette.text.primary,
    '--academy-muted': theme.palette.text.secondary,
    '--academy-paper': theme.palette.background.paper,
  };
  for (const [name, value] of Object.entries(fixed)) vars[`--academy-${name}`] = value;
  return { width: '100%', height: 'auto', display: 'block', ...vars } as CSSProperties;
}
